import { FC, useState } from 'react'

import {
  useGetKycMembersQuery,
  useGetLegacyKycMembersQuery,
} from 'common/services/useKycMember'
import cognito from 'common/cognito'
import { Project } from 'common/project'
import Button from 'components/base/forms/Button'
import Dropdown from 'components/base/forms/Dropdown'
import { useUserData } from 'common/services/useUserData'
import DropdownItem from 'components/base/forms/DropdownItem'
import { useDefaultSubscription } from 'common/services/useDefaultSubscription'

interface Props {
  className?: string
  disabled?: boolean
}

interface DDItemProps {
  label: string
  onClick: () => void
}

const DDItem: FC<DDItemProps> = ({ label, onClick }) => (
  <DropdownItem
    onClick={onClick}
    className='dropdown-item'
    style={{ width: '100%' }}
    role='menuitem'
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    }}
    aria-label={`Generate letter of certification for ${label}`}
  >
    <div className='flex-row align-items-center'>
      <div className='d-flex me-2 flex-fill'>
        <span className='text-nowrap'>{label}</span>
      </div>
    </div>
  </DropdownItem>
)

export async function downloadPDF(
  subscriptionId: number,
  isLegacy?: boolean,
  personId?: string | number,
) {
  try {
    const session = await cognito.getSession()
    const jwtToken = await session!.getAccessToken()

    const baseUrl = `${Project.api}customers/subscriptions/${subscriptionId}/pdf-certification`
    const queryParams = new URLSearchParams(
      personId
        ? {
            [isLegacy ? 'identification_id' : 'person_id']: personId.toString(),
          }
        : {},
    )
    const url = queryParams.toString() ? `${baseUrl}?${queryParams}` : baseUrl

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${jwtToken.getJwtToken()}` },
    })

    // Ensure the response is OK
    if (!response.ok) {
      throw new Error('Failed to fetch the PDF')
    }

    // Get the PDF content as a Blob
    const blob = await response.blob()

    // Create a link element
    const link = document.createElement('a')

    // Create a URL for the Blob
    const urlBlob = URL.createObjectURL(blob)

    // Set the link's href to the Blob URL
    link.href = urlBlob

    // Set the download attribute with the filename you want
    link.download = 'Hoxton Mix Certification.pdf'

    // Append the link to the document body
    document.body.appendChild(link)

    // Programmatically click the link to trigger the download
    link.click()

    // Remove the link from the document
    document.body.removeChild(link)

    // Revoke the object URL to free memory
    URL.revokeObjectURL(urlBlob)
  } catch (error) {
    console.error('Error downloading the PDF:', error)
  }
}

const GenerateLetterOfCertification: FC<Props> = ({
  className,
  disabled: disabledProp,
}) => {
  const { userData } = useUserData()
  const { subscriptionDetail } = useDefaultSubscription()
  const [downloadingPDF, setDownloadingPDF] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  const { data: kycMembersData } = useGetKycMembersQuery(
    { subscription_id: subscriptionDetail?.id! },
    { refetchOnMountOrArgChange: true, skip: !subscriptionDetail?.id },
  )

  const { data: legacyData } = useGetLegacyKycMembersQuery(
    { subscription_id: subscriptionDetail?.id! },
    { refetchOnMountOrArgChange: true, skip: !subscriptionDetail?.id },
  )

  const combinedMembers = [
    ...(kycMembersData?.results || []),
    ...(legacyData?.results || []),
  ]

  const sortedMembers =
    userData?.first_name && userData?.last_name
      ? combinedMembers.sort((a, b) => {
          const isCurrent = (m: any) =>
            m.first_name === userData.first_name &&
            m.last_name === userData.last_name
          return Number(isCurrent(b)) - Number(isCurrent(a))
        })
      : combinedMembers

  const handleDownload = (isLegacy?: boolean, personId?: string | number) => {
    if (!subscriptionDetail?.id) return
    setShowDropdown(false)
    setDownloadingPDF(true)

    downloadPDF(subscriptionDetail.id, isLegacy, personId).finally(() => {
      setDownloadingPDF(false)
    })
  }

  if (!subscriptionDetail) return null

  const isContactInMemberList = sortedMembers.some(
    (member) =>
      `${member.first_name} ${member.last_name}` ===
      subscriptionDetail.contact_name,
  )

  const totalOptions = sortedMembers.length + (!isContactInMemberList ? 1 : 0)

  if (totalOptions === 1) {
    const handleDirectDownload = () => {
      if (!isContactInMemberList) {
        handleDownload()
      } else {
        const member = sortedMembers[0]
        handleDownload('isLegacyKYC' in member, member.id)
      }
    }

    return (
      <Button
        className={className}
        disabled={disabledProp || downloadingPDF || !subscriptionDetail?.id}
        onClick={handleDirectDownload}
        aria-label={
          downloadingPDF
            ? 'Generating letter of certification, please wait'
            : 'Generate letter of certification'
        }
        aria-busy={downloadingPDF}
      >
        Generate Letter of Certification
      </Button>
    )
  }

  return (
    <Dropdown
      dropdownClassName='w-100'
      show={showDropdown}
      setShow={setShowDropdown}
      dropdownContent={
        <div
          style={{ maxHeight: 300, overflowY: 'auto' }}
          role='menu'
          aria-label='Select business owner for letter of certification'
        >
          <div className='text-muted fs-xs p-3 px-4 pb-1' role='presentation'>
            Select a Business Owner
          </div>
          {!isContactInMemberList && (
            <DDItem
              key={subscriptionDetail.id}
              label={subscriptionDetail.contact_name}
              onClick={() => handleDownload()}
            />
          )}

          {sortedMembers.map((member) => (
            <DDItem
              key={member.id}
              label={`${member.first_name} ${member.last_name}`}
              onClick={() => handleDownload('isLegacyKYC' in member, member.id)}
            />
          ))}
        </div>
      }
    >
      <Button
        className={className}
        disabled={disabledProp || downloadingPDF || !subscriptionDetail?.id}
        onClick={() => setShowDropdown(!showDropdown)}
        iconRight={
          <span className='icon fa fa-chevron-down ms-2' aria-hidden='true' />
        }
        aria-label={
          downloadingPDF
            ? 'Generating letter of certification, please wait'
            : `Generate letter of certification. ${totalOptions} business owners available. Select to choose.`
        }
        aria-haspopup='menu'
        aria-expanded={showDropdown}
        aria-busy={downloadingPDF}
      >
        Generate Letter of Certification
      </Button>
    </Dropdown>
  )
}

export default GenerateLetterOfCertification
